/**
 * AppRuntimeCore — React-free app-runtime orchestration, shared by web + Flutter.
 *
 * Extracted from cogo-react-renderer-v2/src/runtime/app-runtime.ts (2187 LOC).
 *
 * Core OWNS (React-free):
 *   - StateManager, ActionFlowEngine, DataSourceManager
 *   - Event-binding store + dispatch (pure TS, no React)
 *   - _currentRoute, blocks/manifest, computed defs
 *   - resolveSymbol, dispatchEvent, navigate/setRoute, runInitFlows,
 *     addBlocks/updateBlocks, registerAllFlows, subscribe, nextGeneration, destroy
 *
 * Core EXCLUDES (web-wrapper only):
 *   - ElementRegistry / getSharedRegistry / buildPageTree / normalizeNavLayout
 *   - React/JSX component glue
 *
 * Web-specific deps are INJECTED via AppRuntimeCoreOptions (default: no-op /
 * undefined). The web wrapper (app-runtime.ts, Task 3) passes the real
 * hostHooks / webmcp / cropImage / sandbox-platform. The Flutter facade passes
 * its CogoBridge platform proxy and omits web-specific deps.
 *
 * Import restriction: ONLY @cogo/runtime-v2 internals + @cogo/types-v2.
 * DO NOT import @cogo/react-renderer, ../host/*, @cogo/webmcp-runtime, ../sandbox/*.
 */

import type {
  App,
  BlockDef,
  EventBinding,
  EventRef,
  Flow,
  FlowMap,
  Page,
  NavigationData,
  DataSource,
  StateContract,
} from "@cogo/types-v2"
import { isAbsolutePosition, parseBindingKey } from "@cogo/types-v2"
import { StateManager, type GovernanceMode } from "./state-manager.js"
import type { AuditEntry } from "./security/audit-log.js"
import { ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY, ActionFlowEngine } from "./action-flow-engine.js"
import type { PlatformAdapter, ToastHandler } from "./action-flow-engine.js"
import { BlockLifecycleManager } from "./block-lifecycle.js"
import { resolveNavFromRoute } from "./nav-sync.js"
import { DataSourceManager, materializeV4DatasetsForRuntime } from "./datasource-manager.js"
import { registerFormulaActions } from "./formula-actions.js"
import { registerReportDesignAction } from "./report-design-action.js"

// ─── EventDispatcher (inlined — React-free pure logic) ──────────────────────
//
// EventDispatcherV2 from cogo-react-renderer-v2/src/runtime/event-dispatcher.ts
// is React-free (imports only EventBinding from @cogo/types-v2). Rather than
// importing across package boundaries (forbidden), the same logic is inlined here.
// Structurally identical to EventDispatcherV2; uses EventBinding from @cogo/types-v2.

class CoreEventDispatcher {
  private handlers = new Map<string, Array<(payload?: unknown) => void>>()
  /** @internal exposed for debug logging in dispatchEvent */
  readonly _bindingsByKey: Record<string, EventBinding[]> = {}

  on(key: string, handler: (payload?: unknown) => void): () => void {
    if (!this.handlers.has(key)) this.handlers.set(key, [])
    this.handlers.get(key)!.push(handler)
    return () => {
      const fns = this.handlers.get(key)
      if (fns) this.handlers.set(key, fns.filter((f) => f !== handler))
    }
  }

  dispatch(elementId: string, eventType: string, payload?: unknown): void {
    const key = `${elementId}:${eventType}`
    this.handlers.get(key)?.forEach((fn) => fn(payload))
  }

  getBinding(
    elementId: string,
    eventType: string,
    activeBlocks?: Set<string>,
  ): EventBinding | undefined {
    const key = `${elementId}:${eventType}`
    const arr = this._bindingsByKey[key]
    if (!arr?.length) return undefined
    if (arr.length === 1) return arr[0]
    if (activeBlocks) {
      const match = arr.find((b) => {
        if (!b.__ns) return false
        if (activeBlocks.has(b.__ns)) return true
        const kebab = b.__ns.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
        return activeBlocks.has(kebab)
      })
      if (match) return match
    }
    return arr[arr.length - 1]
  }

  addBinding(elementId: string, eventType: string, binding: EventBinding): void {
    const key = `${elementId}:${eventType}`
    if (!this._bindingsByKey[key]) {
      this._bindingsByKey[key] = [binding]
      return
    }
    const ns = binding.__ns
    const idx = ns
      ? this._bindingsByKey[key].findIndex((b) => b.__ns === ns)
      : -1
    if (idx >= 0) {
      this._bindingsByKey[key][idx] = binding
    } else {
      this._bindingsByKey[key].push(binding)
    }
  }
}

// ─── Options ─────────────────────────────────────────────────────────────────

export interface AppRuntimeCoreOptions {
  /** Platform adapter (navigation, storage, I/O). Defaults to no-op stubs. */
  platform?: PlatformAdapter

  /** Toast notification hook. Forwarded to ActionFlowEngine. */
  onToast?: ToastHandler

  /** Initial route (defaults to manifest.navigation.defaultRoute or first page). */
  initialRoute?: string

  /** Called on every route change. The web wrapper uses this to load blocks. */
  onNavigate?: (route: string) => void | Promise<void>

  /**
   * Payload-free lifecycle receipt for a declared UI event binding. Receipts
   * are emitted only after the runtime resolves the binding; `completed`
   * means the direct action or ActionFlow promise settled successfully.
   */
  onEventExecution?: (receipt: EventExecutionReceipt) => void

  /** Design system token map (ds.* namespace). */
  designSystem?: Record<string, string>

  /**
   * Write-governance (Step 4 Phase 4.1). Default mode 'off' == today's permissive behavior.
   * See docs/agentic/runtime/step4-checkwrite-governance-design.md. When `mode` is 'audit'/'enforce'
   * pass a real `contract` (e.g. buildEnforcedStateContract) — an empty contract denies all
   * non-local writes. Per-runtime mounts gate this on COGO_RUNTIME_GOVERNANCE.
   */
  governance?: {
    mode?: GovernanceMode
    contract?: StateContract
    onViolation?: (entry: AuditEntry) => void
  }

  /**
   * Injected host-effect hooks (web-only / Flutter-optional).
   * Core wires these to ActionFlowEngine; absent → engine stays headless no-op.
   */
  hostHooks?: {
    onEmitEvent?: (name: string, payload?: unknown) => void
    onOpenModal?: (modalId: string, props: Record<string, unknown>) => void
    onInvokeCallback?: (blockNs: string | null, name: string, args?: unknown) => void
    onMediaControl?: (blockNs: string | null, command: string, args: Record<string, unknown>) => void
    onRunMotion?: (blockNs: string | null, params: Record<string, unknown>) => unknown | Promise<unknown>
  }

  /**
   * cropImage hook — web implementation uses CSS/DOM; Flutter uses native.
   * If absent, cropImage platform action is silently ignored.
   */
  cropImage?: (imageId: string, ratio?: string) => void | Promise<void>

  /**
   * WebMCP lifecycle hooks — called at the end of runInitFlows (after StrictMode
   * destroy/re-mount). The Core calls disposeWebMCP() on destroy().
   * Omit entirely for headless / Flutter (no browser polyfill needed).
   */
  webmcp?: {
    /** (Re-)install the WebMCP polyfill with a reference to the runtime. */
    install: (runtimeRef: AppRuntimeCoreRef) => { dispose: () => void }
  }

  /**
   * Sandbox-aware platform extension (Phase 152/153 Page Editor).
   * When provided, navigation / storage / fetch / print are intercepted.
   */
  sandboxPlatform?: SandboxAwarePlatform
}

/**
 * Minimal interface the web wrapper supplies to the WebMCP installer.
 * Keeps Core from importing WebMCPIntegration types.
 */
export interface AppRuntimeCoreRef {
  stateManager: StateManager
  actionEngine: ActionFlowEngine
  dispatchEvent: (elementId: string, eventType: string, payload?: unknown) => void
}

/** Internal — the web wrapper injects sandbox via this extension of PlatformAdapter. */
export interface SandboxAwarePlatform extends PlatformAdapter {
  sandbox?: {
    navigation?: {
      back: () => void
      openExternal: (url: string) => void
      print: () => void
      history: { back: () => void }
    }
    storage?: {
      local: {
        getItem(k: string): string | null
        setItem(k: string, v: string): void
        removeItem(k: string): void
        clear(): void
      }
    }
    network?: {
      fetch(url: string, init?: RequestInit): Promise<Response>
    }
  }
}

// ─── Public interface ─────────────────────────────────────────────────────────

export interface NavigationReceipt {
  route: string
  loadedBlocks: string[]
  executed: boolean
  status: "completed" | "deduplicated" | "failed"
  error?: string
}

interface EventExecutionReceiptBase {
  schemaVersion: "cogo.runtime-event-execution-receipt.v1"
  executionId: number
  elementId: string
  eventType: string
}

/** A dispatch whose binding resolved to an executable flow or action. */
export interface BoundEventExecutionReceipt extends EventExecutionReceiptBase {
  bindingKind: "action" | "flow"
  bindingRef: string
  status: "accepted" | "completed" | "deduplicated" | "failed"
  errorCode?: "flow-not-found" | "execution-failed" | "navigation-failed"
}

/**
 * A dispatch that reached the runtime and resolved NO executable binding.
 *
 * Emitted for every such dispatch, including the ordinary DOM events renderers
 * forward unconditionally (the AppV2 document keydown bridge). Those are normal
 * no-ops and the runtime cannot tell them apart from a binding that should
 * exist and does not — so it reports the fact rather than judging it. Reporting
 * here instead of on the console is what keeps the normal case quiet: receipts
 * are opt-in, payload-free and filterable by the observer.
 */
export interface UnboundEventExecutionReceipt extends EventExecutionReceiptBase {
  status: "unbound"
  /** `binding-missing`: nothing registered. `binding-invalid`: a binding object with neither flow nor action. */
  errorCode: "binding-missing" | "binding-invalid"
}

export type EventExecutionReceipt =
  | BoundEventExecutionReceipt
  | UnboundEventExecutionReceipt

export interface NavigateOptions {
  force?: boolean
}

interface HostNavigationResult {
  status: "completed" | "failed"
  error?: string
}

export interface AppRuntimeCore {
  readonly stateManager: StateManager
  readonly actionEngine: ActionFlowEngine
  readonly eventDispatcher: CoreEventDispatcher
  readonly lifecycle: BlockLifecycleManager
  readonly currentRoute: string

  navigate(route: string, options?: NavigateOptions): Promise<NavigationReceipt> | NavigationReceipt | void
  syncExternalRoute(route: string): Promise<void>
  resolveSymbol(expr: string): unknown
  dispatchEvent(elementId: string, eventType: string, payload?: unknown): void
  getCurrentPage(): Page
  subscribe(listener: () => void): () => void
  runInitFlows(options?: { isRouteChange?: boolean }): Promise<void>
  /**
   * Fire all `trigger:"tick"` flows for active blocks once, with `{ event: { dt } }`
   * (dt = ms since the last frame). Called by the renderer's requestAnimationFrame
   * clock each frame; this is the runtime half of the `tick` trigger. The matched
   * flow list is cached and invalidated when flows/active-blocks change. A frame is
   * skipped if the previous tick batch is still settling (no overlap/backpressure).
   */
  runTickFlows(dt: number): void
  addBlocks(blocks: Record<string, BlockDef>): string[]
  /** @deprecated Use addBlocks() instead */
  updateBlocks(blocks: Record<string, BlockDef>): void
  registerAllFlows(): void
  updateDesignSystem(designSystem: Record<string, string>): void
  nextGeneration(): number
  destroy(): void
}

// ─── Factory ─────────────────────────────────────────────────────────────────

export function createAppRuntimeCore(
  manifest: App,
  blocks: Record<string, BlockDef>,
  options: AppRuntimeCoreOptions = {},
): AppRuntimeCore {
  return new AppRuntimeCoreImpl(manifest, blocks, options)
}

// ─── Implementation ───────────────────────────────────────────────────────────

class AppRuntimeCoreImpl implements AppRuntimeCore {
  readonly stateManager: StateManager
  readonly actionEngine: ActionFlowEngine
  readonly eventDispatcher: CoreEventDispatcher
  readonly lifecycle: BlockLifecycleManager

  private _currentRoute: string
  private readonly manifest: App
  private readonly options: AppRuntimeCoreOptions

  private readonly designSystem: Record<string, string>
  private readonly computedExprs = new Map<string, string>()
  private readonly flowTimestamps = new Map<string, number>()
  private readonly activeBlocks = new Set<string>()
  /** Cached list of active `trigger:"tick"` flows (null = needs rebuild). Invalidated
   *  on registerAllFlows()/refreshActiveBlocks(). See runTickFlows(). */
  private tickFlows: { name: string; flow: Flow }[] | null = null
  /** Backpressure guard: skip a frame if the previous tick batch hasn't settled. */
  private tickInFlight = false
  private eventExecutionSequence = 0
  private lastNavigate: { route: string; time: number } | null = null
  private readonly listeners = new Set<() => void>()
  private unsubState: (() => void) | null = null
  private webmcpHandle: { dispose: () => void } | null = null

  /** Merchant context extracted from JWT for auto-injection (D-09) */
  private merchantId: string | null = null

  private readonly dataSourceManager: DataSourceManager

  /** Convenience getter — delegates to lifecycle manager */
  private get blocks(): Record<string, BlockDef> {
    return this.lifecycle.getAllBlocks()
  }

  constructor(
    manifest: App,
    blocks: Record<string, BlockDef>,
    options: AppRuntimeCoreOptions,
  ) {
    this.manifest = manifest
    this.options = options
    this.designSystem = options.designSystem ?? {}

    const pages = manifest.pages ?? []
    const defaultRoute =
      manifest.navigation?.defaultRoute ?? pages[0]?.route ?? "/"
    this._currentRoute = options.initialRoute ?? defaultRoute

    // ── 1. StateManager ───────────────────────────────────────────────────
    // Write-governance (Step 4): default mode 'off' is byte-for-byte today's `{ permissive: true }`.
    // Contract precedence (Phase 4.2): explicit options.governance.contract → the artifact's baked
    // `manifest.meta.stateContract` (from buildEnforcedStateContract at assemble time) → emptyContract.
    // The contract is inert until a runtime opts into mode 'audit'/'enforce'.
    const emptyContract = {
      localState: {},
      inputSymbols: [],
      outputSymbols: [],
    }
    const gov = options.governance
    this.stateManager = new StateManager(
      { state: manifest.globalState ?? {} },
      gov?.contract ?? manifest.meta?.stateContract ?? emptyContract,
      { mode: gov?.mode ?? "off", onViolation: gov?.onViolation },
    )

    // ── 2. ActionFlowEngine + platform adapter ────────────────────────────
    const basePlatform = (options.sandboxPlatform ?? options.platform) as
      | SandboxAwarePlatform
      | undefined

    const cropImageFn = options.cropImage
    const platformAdapter: PlatformAdapter = {
      // Navigation — thread the optional transition + push/replace intent through
      // setRoute so they reach the host router (see setRoute's host-notify).
      pushRoute: (route, options) => { this.setRoute(route, options) },
      replaceRoute: (route, options) => { this.setRoute(route, { ...options, replace: true }) },
      goBack: () => {
        if (basePlatform?.sandbox?.navigation) {
          basePlatform.sandbox.navigation.history.back()
          return
        }
        if (basePlatform?.goBack) basePlatform.goBack()
        else if (typeof window !== "undefined") window.history.back()
      },
      openExternal: (url, newTab) => {
        if (basePlatform?.sandbox?.navigation && newTab === false) {
          basePlatform.sandbox.navigation.openExternal(url)
          return
        }
        if (basePlatform?.openExternal) {
          basePlatform.openExternal(url, newTab)
          return
        }
        if (typeof window !== "undefined" && url) {
          if (newTab !== false) {
            const win = window.open(url, "_blank", "noopener,noreferrer")
            if (!win) window.location.href = url
          } else {
            window.location.href = url
          }
        }
      },
      getCurrentPath: () => this._currentRoute,
      // Storage
      getItem: (k) =>
        basePlatform?.sandbox?.storage?.local.getItem(k) ??
        basePlatform?.getItem?.(k) ??
        (typeof localStorage !== "undefined" ? localStorage.getItem(k) : null),
      setItem: (k, v) => {
        if (basePlatform?.sandbox?.storage) {
          basePlatform.sandbox.storage.local.setItem(k, v)
          return
        }
        if (basePlatform?.setItem) basePlatform.setItem(k, v)
        else if (typeof localStorage !== "undefined") localStorage.setItem(k, v)
      },
      removeItem: (k) => {
        if (basePlatform?.sandbox?.storage) {
          basePlatform.sandbox.storage.local.removeItem(k)
          return
        }
        if (basePlatform?.removeItem) basePlatform.removeItem(k)
        else if (typeof localStorage !== "undefined") localStorage.removeItem(k)
      },
      clearStorage: () => {
        if (basePlatform?.sandbox?.storage) {
          basePlatform.sandbox.storage.local.clear()
          return
        }
        if (basePlatform?.clearStorage) basePlatform.clearStorage()
        else if (typeof localStorage !== "undefined") localStorage.clear()
      },
      copyToClipboard: (t) =>
        basePlatform?.copyToClipboard?.(t) ??
        (typeof navigator !== "undefined"
          ? navigator.clipboard.writeText(t)
          : Promise.resolve()),
      readClipboard: () =>
        basePlatform?.readClipboard?.() ??
        (typeof navigator !== "undefined"
          ? navigator.clipboard.readText()
          : Promise.resolve("")),
      showToast: (p) => basePlatform?.showToast?.(p),
      setTheme: (t) => basePlatform?.setTheme?.(t),
      showPopup: (p) => basePlatform?.showPopup?.(p),
      showLoading: (s) => basePlatform?.showLoading?.(s),
      scrollToElement: (id, s) => {
        if (basePlatform?.scrollToElement) basePlatform.scrollToElement(id, s)
        else {
          const addressed = document.getElementById(id) ?? Array.from(
            document.querySelectorAll<HTMLElement>("[data-cogo-element-id], [data-id]"),
          ).find((candidate) =>
            candidate.getAttribute("data-cogo-element-id") === id ||
            candidate.getAttribute("data-id") === id,
          )
          const target = addressed?.matches?.(".contents")
            ? addressed.querySelector<HTMLElement>(
              "input, textarea, select, button, a[href], [role], [tabindex], [id], *",
            ) ?? addressed
            : addressed
          target?.scrollIntoView(s ? { behavior: "smooth" } : undefined)
        }
      },
      scrollToPosition: (y, s) => {
        if (basePlatform?.scrollToPosition) basePlatform.scrollToPosition(y, s)
        else window.scrollTo({ top: y, behavior: s ? "smooth" : "auto" })
      },
      getScrollY: () =>
        basePlatform?.getScrollY?.() ??
        (typeof window !== "undefined" ? window.scrollY : 0),
      print: () => {
        if (basePlatform?.sandbox?.navigation) {
          basePlatform.sandbox.navigation.print()
          return
        }
        if (basePlatform?.print) basePlatform.print()
        else if (typeof window !== "undefined") window.print()
      },
      share: (d) => basePlatform?.share?.(d) ?? Promise.resolve(),
      getGeolocation: () =>
        basePlatform?.getGeolocation?.() ??
        Promise.resolve({ latitude: 0, longitude: 0 }),
      getUserMedia: (c) =>
        basePlatform?.getUserMedia?.(c) ?? Promise.resolve(null),
      download: (u, f) => basePlatform?.download?.(u, f),
      pickFile: (a) => basePlatform?.pickFile?.(a) ?? Promise.resolve(null),
      showNotification: (t, b) => basePlatform?.showNotification?.(t, b),
      digest: (a, d) =>
        basePlatform?.digest?.(a, d) ?? Promise.resolve(""),
      fetch: (u, o) => {
        if (basePlatform?.sandbox?.network)
          return basePlatform.sandbox.network.fetch(u, o)
        return basePlatform?.fetch?.(u, o) ?? globalThis.fetch(u, o)
      },
      createEventSource: (u, cb) =>
        basePlatform?.createEventSource?.(u, cb) ?? { close() {} },
      createWebSocket: (u) =>
        basePlatform?.createWebSocket?.(u) ?? {
          send() {},
          close() {},
          onMessage() {},
          onClose() {},
        },
      dispatchAnalyticsEvent: (e, d) =>
        basePlatform?.dispatchAnalyticsEvent?.(e, d),
      openAuthSession: basePlatform?.openAuthSession
        ? (u, r) => basePlatform.openAuthSession!(u, r)
        : undefined,
      // Native signature agent (D3/SCOPE-1). Forwarded on the same terms as
      // openAuthSession: this adapter literal is what the engine actually sees, so
      // an unforwarded optional method is a host capability the runtime can never
      // reach — leaving `invokeNativeSignatureAgent` out here would make every
      // invocation answer "no-native-agent" even on a host that registered agents.
      invokeNativeSignatureAgent: basePlatform?.invokeNativeSignatureAgent
        ? (method, agentRef, payload) =>
            basePlatform.invokeNativeSignatureAgent!(method, agentRef, payload)
        : undefined,
      cropImage: (imageId: string, ratio?: string) => {
        if (basePlatform?.cropImage) return basePlatform.cropImage(imageId, ratio)
        if (cropImageFn) return cropImageFn(imageId, ratio)
        // no-op when no cropImage provider (headless / Flutter without native crop)
      },
    }

    this.actionEngine = new ActionFlowEngine(this.stateManager, platformAdapter)
    if (options.onToast) this.actionEngine.onToast = options.onToast

    // Wire the four host-effect hooks from injected hostHooks bundle (if provided).
    // Otherwise fall back to individual hook properties on the options object.
    // Absent both → engine stays headless no-op.
    if (options.hostHooks) {
      const hh = options.hostHooks
      if (hh.onEmitEvent) this.actionEngine.onEmitEvent = hh.onEmitEvent
      if (hh.onOpenModal) this.actionEngine.onOpenModal = hh.onOpenModal
      if (hh.onInvokeCallback) {
        this.actionEngine.onInvokeCallback = (name, args, context) =>
          hh.onInvokeCallback!(executionBlockNamespace(context), name, args)
      }
      if (hh.onMediaControl) {
        this.actionEngine.onMediaControl = (command, args, context) =>
          hh.onMediaControl!(executionBlockNamespace(context), command, args)
      }
      if (hh.onRunMotion) {
        this.actionEngine.onRunMotion = (params, context) =>
          hh.onRunMotion!(executionBlockNamespace(context), params)
      }
    }

    // ── 3. DataSourceManager ──────────────────────────────────────────────
    this.dataSourceManager = new DataSourceManager(
      { ...(this.manifest.datasources ?? {}) } as unknown as ConstructorParameters<
        typeof DataSourceManager
      >[0],
      "",
      {
        fetch: (u, init) => platformAdapter.fetch(u, init),
        extraHeaders: () => this.getMerchantHeaders(),
      },
    )
    this.actionEngine.setDataSourceManager(this.dataSourceManager)

    // ── 4. EventDispatcher ────────────────────────────────────────────────
    this.eventDispatcher = new CoreEventDispatcher()

    // ── 5. BlockLifecycleManager ──────────────────────────────────────────
    this.lifecycle = new BlockLifecycleManager({
      initBlockState: (name, block) =>
        this.stateManager.runTrusted(() => this.initSingleBlockState(name, block)),
      registerBlockFlows: (name, block) =>
        this.registerSingleBlockFlows(name, block),
      registerBlockBindings: (name, block) => {
        const rawNs = block.meta?.namespace ?? name
        const ns = camelToKebab(rawNs)
        this.registerBlockBindings(block, ns)
      },
      executeFlow: (flow, ctx) => this.actionEngine.executeFlow(flow, ctx),
      findFlow: (name) => this.actionEngine.findFlow(name),
      getRegisteredFlowNames: () => this.actionEngine.getRegisteredFlowNames(),
    })

    // ── Initialize manifest-level state (trusted: runtime seeding bypasses governance, R6) ──
    this.stateManager.runTrusted(() => this.initializeManifestState())
    this.registerBuiltinActions(platformAdapter)

    // ── Add initial blocks ─────────────────────────────────────────────────
    this.lifecycle.addBlocks(blocks)

    // State subscription — re-eval computed on every change (trusted: computed.* is runtime-derived)
    this.unsubState = this.stateManager.subscribe("", () => {
      this.stateManager.runTrusted(() => {
        for (const [key, expr] of this.computedExprs) {
          const resolved = this.stateManager.resolve(expr)
          this.stateManager.setStateQuiet(`computed.${key}`, resolved)
        }
      })
      this.notifyListeners()
    })

    // D-09: Extract merchantId from JWT
    this.setMerchantContext()
  }

  get currentRoute(): string {
    return this._currentRoute
  }

  // ─── Merchant Context (D-09) ───────────────────────────────────────────────

  private setMerchantContext(): void {
    try {
      const cookieStr =
        typeof document !== "undefined" ? document.cookie || "" : ""
      const match = cookieStr.match(/partner-auth-token=([^;]+)/)
      if (match) {
        const payload = JSON.parse(atob(match[1].split(".")[1]))
        this.merchantId =
          payload.app_metadata?.merchant_id ?? payload.merchantId ?? null
      }
    } catch {
      // No merchant context available (SSR or non-merchant user)
    }
  }

  private getMerchantHeaders(): Record<string, string> {
    if (!this.merchantId) return {}
    return { "X-Merchant-Id": this.merchantId }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  navigate(
    route: string,
    options: NavigateOptions = {},
  ): Promise<NavigationReceipt> | NavigationReceipt | void {
    if (route.startsWith("__action:")) {
      const flowName = route.slice("__action:".length)
      const flow = this.actionEngine.findFlow(flowName)
      if (flow) {
        // notifyListeners in finally: pre-failure state changes must still
        // re-render, and a rejecting step (e.g. a restApi fetch failure) must
        // not surface as an unhandled rejection — same contract as the
        // dispatchEvent path below.
        this.actionEngine
          .executeFlow(flow, {})
          .catch((err) => {
            console.error(
              "[AppRuntimeCore Flow Error] Failed to execute __action flow " + flowName,
              err,
            )
          })
          .finally(() => this.notifyListeners())
      } else {
        console.warn(
          `[AppRuntimeCore] __action flow not found: "${flowName}"`,
        )
      }
      return
    }
    const now = Date.now()
    if (
      !options.force &&
      this.lastNavigate &&
      this.lastNavigate.route === route &&
      now - this.lastNavigate.time < 300
    )
      return {
        route,
        loadedBlocks: this.getCurrentPage()?.blocks ?? [],
        executed: false,
        status: "deduplicated",
      }
    this.lastNavigate = { route, time: now }
    // Return a receipt so WebMCP and platform facades can prove whether navigation
    // actually ran through init flows and listener notification. Existing callers
    // that ignore the return value are unaffected.
    return this.setRoute(route)
      .then<NavigationReceipt>(async ({ loadedBlocks, navigationReady }) => {
        const hostNavigation = await navigationReady
        if (hostNavigation.status === "failed") {
          return {
            route,
            loadedBlocks,
            executed: true,
            status: "failed",
            error: hostNavigation.error,
          }
        }
        return {
          route,
          loadedBlocks,
          executed: true,
          status: "completed",
        }
      })
      .catch((error: unknown): NavigationReceipt => ({
        route,
        loadedBlocks: [],
        executed: false,
        status: "failed",
        error: error instanceof Error ? error.message : String(error),
      }))
  }

  async syncExternalRoute(route: string): Promise<void> {
    if (this._currentRoute === route) return
    this.lastNavigate = null
    await this.setRoute(route, { external: true })
  }

  resolveSymbol(expr: string): unknown {
    if (typeof expr === "string" && expr.includes("@{")) {
      const pureLocal = /^@\{([^}]+)\}$/.exec(expr)
      if (pureLocal) {
        const path = pureLocal[1].trim()
        if (path.startsWith("!"))
          return !this.stateManager.resolve(`#{${path.slice(1)}}`)
        return this.stateManager.resolve(`#{${path}}`)
      }
      expr = expr.replace(/@\{([^}]+)\}/g, (_, inner) => {
        const path = (inner as string).trim()
        if (path.startsWith("!")) {
          const val = this.stateManager.resolve(`#{${path.slice(1)}}`)
          return String(!val)
        }
        const val = this.stateManager.resolve(`#{${path}}`)
        return val != null ? String(val) : ""
      })
      if (!expr.includes("#{")) return expr
    }

    if (typeof expr === "string") {
      const computedMatch = /^#\{computed\.(\w+)\}$/.exec(expr)
      if (computedMatch) {
        const key = computedMatch[1]
        const originalExpr = this.computedExprs.get(key)
        if (originalExpr) {
          const resolved = this.stateManager.resolve(originalExpr)
          this.stateManager.runTrusted(() =>
            this.stateManager.setStateQuiet(`computed.${key}`, resolved),
          )
          return resolved
        }
      }
    }

    return this.stateManager.resolve(expr) ?? ""
  }

  dispatchEvent(
    elementId: string,
    eventType: string,
    payload?: unknown,
  ): void {
    this.eventDispatcher.dispatch(elementId, eventType, payload)

    const payloadObj =
      payload != null && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : {}

    let binding = this.eventDispatcher.getBinding(
      elementId,
      eventType,
      this.activeBlocks,
    )
    let effectiveEventType = eventType
    if (!binding && eventType === "tap") {
      binding = this.eventDispatcher.getBinding(
        elementId,
        "navigate",
        this.activeBlocks,
      )
      if (binding) effectiveEventType = "navigate"
    }
    const executionId = ++this.eventExecutionSequence
    const emitUnbound = (
      errorCode: UnboundEventExecutionReceipt["errorCode"],
    ) => {
      try {
        this.options.onEventExecution?.({
          schemaVersion: "cogo.runtime-event-execution-receipt.v1",
          executionId,
          elementId,
          eventType: effectiveEventType,
          status: "unbound",
          errorCode,
        })
      } catch {
        // Observability must never alter application execution.
      }
    }

    // Renderers deliberately forward ordinary DOM events (for example the
    // document-level keydown bridge) even when an app has not declared a Cogo
    // binding for them. An absent binding is therefore a normal no-op, not a
    // runtime problem — so it stays silent on the console and execution is
    // unchanged. It is still RECORDED on the opt-in `onEventExecution` seam,
    // because the runtime cannot distinguish that normal no-op from a binding
    // that should exist and does not; without the receipt the two are the same
    // event and the same silence, and "this element dispatched and nothing was
    // bound" is unobservable from outside. A payload-free receipt an observer
    // filters is the version of that report that does not make the normal case
    // noisy. Keep the console warning for the actionable case where a binding
    // exists but is malformed and has no executable target.
    if (!binding) {
      emitUnbound("binding-missing")
      return
    }
    if (!binding.flow && !binding.action) {
      emitUnbound("binding-invalid")
      console.warn(
        `[AppRuntimeCore] Invalid binding for ${elementId}:${eventType}: expected flow or action`,
        {
          binding,
          activeBlocks: [...this.activeBlocks],
          totalBindings: Object.keys(this.eventDispatcher._bindingsByKey)
            .length,
        },
      )
      return
    }
    const resolvedBinding = binding
    const flowName = resolvedBinding.flow as string | undefined
    const bindingKind = resolvedBinding.action ? "action" : "flow"
    const bindingRef = String(resolvedBinding.action ?? flowName)
    const emitReceipt = (
      status: BoundEventExecutionReceipt["status"],
      errorCode?: BoundEventExecutionReceipt["errorCode"],
    ) => {
      try {
        this.options.onEventExecution?.({
          schemaVersion: "cogo.runtime-event-execution-receipt.v1",
          executionId,
          elementId,
          eventType: effectiveEventType,
          bindingKind,
          bindingRef,
          status,
          ...(errorCode ? { errorCode } : {}),
        })
      } catch {
        // Observability must never alter application execution.
      }
    }
    emitReceipt("accepted")

    // Built-in navigate action (with dedup guard)
    if (flowName?.startsWith("__navigate:")) {
      const resolvedRoute = payloadObj.__resolvedRoute as string | undefined
      const route = resolvedRoute ?? flowName.slice("__navigate:".length)
      const now = Date.now()
      if (
        this.lastNavigate &&
        this.lastNavigate.route === route &&
        now - this.lastNavigate.time < 300
      ) {
        emitReceipt("deduplicated")
        return
      }
      this.lastNavigate = { route, time: now }
      void this.setRoute(route)
        .then(async ({ navigationReady }) => {
          const navigation = await navigationReady
          emitReceipt(
            navigation.status === "completed" ? "completed" : "failed",
            navigation.status === "failed" ? "navigation-failed" : undefined,
          )
        })
        .catch(() => emitReceipt("failed", "navigation-failed"))
      return
    }

    // Flow debounce for tap events (300ms)
    if (eventType === "tap") {
      const lockKey = `${elementId}:${effectiveEventType}`
      const now = Date.now()
      const lastExec = this.flowTimestamps.get(lockKey)
      if (lastExec && now - lastExec < 300) {
        emitReceipt("deduplicated")
        return
      }
      this.flowTimestamps.set(lockKey, now)
    }

    const buildFlowContext = (): Record<string, unknown> => {
      const { __resolvedRoute: _, ...itemContext } = payloadObj
      const rawEvent =
        payload != null && typeof payload === "object"
          ? (payload as Record<string, unknown>)
          : { value: payload }
      const eventParams = resolvedBinding.params
        ? {
            ...rawEvent,
            ...resolveBindingParams(
              resolvedBinding.params,
              { ...itemContext, event: rawEvent },
              this.stateManager,
            ),
          }
        : payload != null
          ? typeof payload === "object"
            ? payload
            : { value: payload }
          : {}
      return { event: eventParams, ...itemContext }
    }

    const blockNs = resolvedBinding.__ns
    if (resolvedBinding.action) {
      const directAction = resolvedBinding.action
      const directParams =
        directAction === "navigate" && typeof payloadObj.__resolvedRoute === "string"
          ? { ...(resolvedBinding.params ?? {}), route: payloadObj.__resolvedRoute }
          : (resolvedBinding.params ?? {})
      this.actionEngine
        .executeFlow(
          {
            name: `__direct:${elementId}:${effectiveEventType}`,
            trigger: "event",
            steps: [{ type: "basic", action: directAction, params: directParams }],
          } as Flow,
          {
            ...buildFlowContext(),
            [ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY]: blockNs ?? null,
          },
        )
        .then(() => {
          this.notifyListeners()
          emitReceipt("completed")
        })
        .catch((err) => {
          console.error(`[AppRuntimeCore Direct Action Error] Failed to execute ${directAction}`, err)
          emitReceipt("failed", "execution-failed")
        })
      return
    }

    // Resolve flow: block-scoped → exact → suffix match
    const resolvedFlow =
      (blockNs
        ? this.actionEngine.findFlow(`${blockNs}.${flowName!}`)
        : undefined) ??
      this.actionEngine.findFlow(flowName!) ??
      this.findFlowBySuffix(flowName!)
    if (!resolvedFlow) {
      console.warn(
        `[AppRuntimeCore] Flow not found: "${flowName}" (ns: ${blockNs})`,
      )
      emitReceipt("failed", "flow-not-found")
      return
    }

    const flowContext = buildFlowContext()

    this.actionEngine
      .executeFlow(resolvedFlow, flowContext)
      .then(() => {
        this.notifyListeners()
        emitReceipt("completed")
      })
      .catch((err) => {
        console.error(
          "[AppRuntimeCore Flow Error] Failed to execute flow " + flowName,
          err,
        )
        emitReceipt("failed", "execution-failed")
      })
  }

  getCurrentPage(): Page {
    const pages = this.manifest.pages ?? []
    if (pages.length === 0) {
      return {
        id: "__default",
        route: this._currentRoute || "/",
        layout: "stack",
        blocks: Object.keys(this.blocks),
        blockLayout: {},
      } as Page
    }
    const matches = pages
      .map((p, idx) => ({
        p,
        idx,
        score: scorePageSpecificity(p.route, this._currentRoute),
      }))
      .filter((m) => m.score >= 0)
    if (matches.length === 0) return pages[0]
    matches.sort((a, b) => b.score - a.score || a.idx - b.idx)
    return matches[0].p
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  runTickFlows(dt: number): void {
    // Rebuild the active tick-flow list when invalidated (once per flow/active-block change),
    // mirroring activatePage's collect-by-trigger loop (block-lifecycle.ts) but scoped to "tick".
    if (this.tickFlows === null) {
      const matched: { name: string; flow: Flow }[] = []
      for (const flowName of this.actionEngine.getRegisteredFlowNames()) {
        const flow = this.actionEngine.findFlow(flowName)
        if (!flow || flow.trigger !== "tick") continue
        const ns = flowName.split(".")[0]
        if (
          this.activeBlocks.size > 0 &&
          !this.activeBlocks.has(ns) &&
          !this.activeBlocks.has(camelToKebab(ns)) &&
          !this.activeBlocks.has(kebabToCamel(ns))
        )
          continue
        matched.push({ name: flowName, flow })
      }
      this.tickFlows = matched
    }
    if (this.tickFlows.length === 0 || this.tickInFlight) return
    this.tickInFlight = true
    const context = { event: { dt } }
    void Promise.all(
      this.tickFlows.map(({ name, flow }) =>
        Promise.resolve(this.actionEngine.executeFlow(flow, context)).catch((err) =>
          console.error("[AppRuntimeCore tick]", name, err),
        ),
      ),
    ).finally(() => {
      this.tickInFlight = false
    })
  }

  runInitFlows(options?: { isRouteChange?: boolean }): Promise<void> {
    // (Re-)install WebMCP polyfill — idempotent, survives StrictMode
    if (this.options.webmcp) {
      this.webmcpHandle?.dispose()
      this.webmcpHandle = this.options.webmcp.install({
        stateManager: this.stateManager,
        actionEngine: this.actionEngine,
        dispatchEvent: (id, type, payload) =>
          this.dispatchEvent(id, type, payload),
      })
    }

    const page = this.getCurrentPage()
    if (!page) return Promise.resolve()

    // routeParams is used both inside the trusted region (state writes) and after it (the init-flow
    // context below), so it must live outside the runTrusted closure.
    const routeParams = extractRouteParams(page.route, this._currentRoute)

    // Route/system-state setup + active-block default re-seed = trusted runtime writes (Step 4 R6).
    // NOTE: this region must NOT contain user-flow execution — those run later via activatePage().
    this.stateManager.runTrusted(() => {
    // Store current route in state
    this.stateManager.setState(
      "_system.navigation.currentRoute",
      this._currentRoute,
    )
    this.stateManager.setState(
      "appData._system.navigation.currentRoute",
      this._currentRoute,
    )

    this.stateManager.setState("_system.navigation.params", routeParams)
    this.stateManager.setState("route", {
      params: routeParams,
      path: this._currentRoute,
    })

    // Store page name for breadcrumbs
    const pageMeta = page as unknown as Record<string, unknown>
    const resolvedPageName =
      pageMeta.name ?? pageMeta.title ?? page.id
    this.stateManager.setState(
      "_system.navigation.pageName",
      resolvedPageName,
    )
    this.stateManager.setState(
      "appData._system.navigation.pageName",
      resolvedPageName,
    )

    // Determine active blocks for this page
    this.refreshActiveBlocks()

    // Auto-sync navigation state from route
    this.syncNavigation()

    // Re-apply active page block variable defaults (skip globalState keys).
    // Only on route change — skip on addBlocks() to preserve flow-populated data.
    if (options?.isRouteChange !== false) {
      const globalStateKeys = new Set(
        Object.keys(this.manifest.globalState ?? {}),
      )
      const currentPage = this.getCurrentPage()
      const layoutBlockName = currentPage?.layout
      for (const blockName of this.activeBlocks) {
        if (
          layoutBlockName &&
          (blockName === layoutBlockName ||
            camelToKebab(blockName) === layoutBlockName ||
            kebabToCamel(blockName) === layoutBlockName)
        )
          continue
        const block = this.blocks[blockName]
        if (!block) continue
        const blockState = block.state as Record<string, unknown>
        const stateEntries = (blockState?.state ?? {}) as Record<
          string,
          unknown
        >
        for (const [key, varDef] of Object.entries(stateEntries)) {
          if (
            globalStateKeys.has(key) &&
            this.stateManager.get(key) != null
          )
            continue
          const resolved = resolveVarDefault(varDef)
          if (resolved !== undefined) {
            const existing = this.stateManager.get(key)
            if (
              existing != null &&
              typeof existing === "object" &&
              !Array.isArray(existing) &&
              resolved != null &&
              typeof resolved === "object" &&
              !Array.isArray(resolved)
            ) {
              if (key === "appData") {
                const merged = { ...(resolved as Record<string, unknown>) }
                const existingObj = existing as Record<string, unknown>
                for (const [subKey, subVal] of Object.entries(existingObj)) {
                  if (subVal != null) merged[subKey] = subVal
                }
                this.stateManager.setState(key, merged)
              } else {
                this.stateManager.setState(key, {
                  ...(existing as Record<string, unknown>),
                  ...(resolved as Record<string, unknown>),
                })
              }
            } else {
              this.stateManager.setState(key, resolved)
            }
          }
        }
      }
    }
    })

    // Re-register all flows (ensures callFlow works after route change)
    this.registerAllFlows()

    // Load REST datasources before init flows
    const restPromises: Promise<void>[] = [this.loadRestDataSources()]
    for (const blockName of this.activeBlocks) {
      const block = this.blocks[blockName]
      if (block)
        restPromises.push(
          this.loadBlockRestDataSources(blockName, block),
        )
    }

    const context: Record<string, unknown> = {
      route: { params: routeParams, path: this._currentRoute },
    }
    return Promise.all(restPromises).then(() =>
      this.lifecycle.activatePage(this.activeBlocks, page.id, context),
    )
  }

  addBlocks(blocks: Record<string, BlockDef>): string[] {
    const added = this.lifecycle.addBlocks(blocks)
    if (added.length > 0) {
      this.refreshActiveBlocks()
      const page = this.getCurrentPage()
      if (page) {
        const pageBlocks = new Set([
          ...(page.blocks ?? []),
          ...(this.manifest.globalBlocks ?? []),
        ])
        const hasNewPageBlock = added.some(
          (name) =>
            pageBlocks.has(name) ||
            pageBlocks.has(kebabToCamel(name)) ||
            pageBlocks.has(camelToKebab(name)),
        )
        if (hasNewPageBlock) {
          this.runInitFlows({ isRouteChange: false })
        }
      }
    }
    return added
  }

  /** @deprecated Use addBlocks() instead */
  updateBlocks(blocks: Record<string, BlockDef>): void {
    this.lifecycle.addBlocks(blocks)
  }

  nextGeneration(): number {
    return this.lifecycle.nextGeneration()
  }

  destroy(): void {
    // Clean up realtime subscriptions (RT-02)
    try {
      const registry = this.stateManager.get("_subscriptionRegistry")
      if (
        registry &&
        typeof (registry as Record<string, unknown>).unsubscribeAll ===
          "function"
      ) {
        ;(
          registry as { unsubscribeAll: () => Promise<void> }
        )
          .unsubscribeAll()
          .catch((err: unknown) =>
            console.warn(
              "[AppRuntimeCore] realtime cleanup failed:",
              err,
            ),
          )
      }
    } catch {
      /* ignore cleanup errors */
    }

    // Clean up auth state change subscription (Phase 73)
    try {
      const authSub = this.stateManager.get(
        "_subscriptions.authStateChange",
      )
      if (
        authSub &&
        typeof (authSub as Record<string, unknown>).unsubscribe ===
          "function"
      ) {
        ;(authSub as { unsubscribe: () => void }).unsubscribe()
      }
    } catch {
      /* ignore cleanup errors */
    }

    this.unsubState?.()
    this.listeners.clear()
    this.lifecycle.destroy()
    this.webmcpHandle?.dispose()
    this.webmcpHandle = null
  }

  // ─── Private: routing ──────────────────────────────────────────────────────

  private async setRoute(
    route: string,
    options?: { transition?: string; replace?: boolean; external?: boolean },
  ): Promise<{
    loadedBlocks: string[]
    navigationReady: Promise<HostNavigationResult>
  }> {
    this._currentRoute = route
    const navPromise = options?.external
      ? Promise.resolve<HostNavigationResult>({ status: "completed" })
      : Promise.resolve(this.options.onNavigate?.(route)).then<
          HostNavigationResult,
          HostNavigationResult
        >(
          () => ({ status: "completed" }),
          (error: unknown) => {
            console.warn(
              `[AppRuntimeCore] onNavigate("${route}") failed:`,
              error,
            )
            return {
              status: "failed",
              error: error instanceof Error ? error.message : String(error),
            }
          },
        )
    // Forward the optional transition + push/replace intent to the host router. Only
    // pass the options arg when a transition is present so the common case stays a
    // clean single-arg pushRoute(route) call (preserves existing host-adapter
    // contracts/assertions). `replace` selects replaceRoute over pushRoute.
    const navOptions = options?.transition ? { transition: options.transition } : undefined
    if (!options?.external) {
      if (options?.replace) {
        if (navOptions) this.options.platform?.replaceRoute?.(route, navOptions)
        else this.options.platform?.replaceRoute?.(route)
      } else if (navOptions) {
        this.options.platform?.pushRoute?.(route, navOptions)
      } else {
        this.options.platform?.pushRoute?.(route)
      }
    }
    this.lifecycle.resetForRoute()
    await this.runInitFlows()
    this.notifyListeners()
    const page = this.getCurrentPage()
    return {
      loadedBlocks: page?.blocks ?? [],
      navigationReady: navPromise,
    }
  }

  private refreshActiveBlocks(): void {
    const page = this.getCurrentPage()
    if (!page) return
    this.activeBlocks.clear()
    this.tickFlows = null // active-block set changing → rebuild tick-flow scope
    const addBoth = (name: string) => {
      this.activeBlocks.add(name)
      this.activeBlocks.add(kebabToCamel(name))
      this.activeBlocks.add(camelToKebab(name))
    }
    for (const name of this.manifest.globalBlocks ?? []) addBoth(name)
    for (const name of page.blocks ?? []) addBoth(name)
    if (
      page.layout &&
      page.layout !== "stack" &&
      page.layout !== "sidebar"
    ) {
      addBoth(page.layout)
    }
  }

  private syncNavigation(): void {
    const page = this.getCurrentPage()
    if (!page?.layout) return
    if (page.layout === "sidebar" || page.layout === "stack") return
    const layoutBlock = this.blocks[page.layout]
    if (!layoutBlock) return
    const navData = (
      layoutBlock.data as Record<string, unknown> | undefined
    )?.navigation as NavigationData | undefined
    if (!navData || navData.sync !== "auto") return

    const result = resolveNavFromRoute(
      navData,
      this._currentRoute,
      navData.activeMatch ?? "prefix",
    )
    if (!result) return

    const map = navData.stateMap ?? {}
    if (map.activeTab) {
      this.stateManager.setState(map.activeTab, result.tabSlug)
      this.stateManager.setState(
        "appData._system.navigation.activeTopTab",
        result.tabSlug,
      )
    }
    if (map.activeItem) {
      this.stateManager.setState(map.activeItem, result.pageTitle)
      this.stateManager.setState(
        `appData.${map.activeItem}`,
        result.pageTitle,
      )
    }
    if (map.sectionLabel) {
      this.stateManager.setState(map.sectionLabel, result.tabTitle)
      this.stateManager.setState(
        `appData.${map.sectionLabel}`,
        result.tabTitle,
      )
    }
    if (map.breadcrumb)
      this.stateManager.setState(map.breadcrumb, result.breadcrumb)
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) listener()
  }

  // ─── Private: state initialization ────────────────────────────────────────

  private initializeManifestState(): void {
    const sm = this.stateManager

    sm.registerNamespaceAlias("ui")
    sm.registerNamespaceAlias("state")
    sm.registerNamespaceAlias("computed")
    // `local` is the canonical local-state prefix: the validator REQUIRES bare setState targets
    // (writing the root/flat key), so `#{local.X}` reads must resolve to that same flat key —
    // register `local` as an alias so get("local.X") falls back to get("X"). Without this, a
    // generated setState("count") (root) and a #{local.count} display read different namespaces
    // and the UI never updates (the L3 dead-display bug). [#798 2785deb72 port → Core]
    sm.registerNamespaceAlias("local")

    if (this.manifest.appData) {
      for (const [key, value] of Object.entries(this.manifest.appData)) {
        sm.setState(`appData.${key}`, structuredClone(value))
      }
    }

    if (this.manifest.globalState) {
      for (const [key, def] of Object.entries(this.manifest.globalState)) {
        const varDef = def as unknown as Record<string, unknown>
        if (varDef.default !== undefined) {
          const existing = sm.get(key)
          if (
            key === "appData" &&
            existing !== null &&
            typeof existing === "object" &&
            !Array.isArray(existing) &&
            varDef.default !== null &&
            typeof varDef.default === "object" &&
            !Array.isArray(varDef.default)
          ) {
            sm.setState(key, {
              ...(structuredClone(varDef.default) as Record<string, unknown>),
              ...(existing as Record<string, unknown>),
            })
          } else {
            sm.setState(key, structuredClone(varDef.default))
          }
        }
      }
    }

    for (const [name, ds] of Object.entries(
      this.manifest.datasources ?? {},
    )) {
      if (
        ds.type !== "static" &&
        ds.type !== "rest" &&
        ds.type !== "restApi"
      )
        continue
      const seed =
        ds.data !== undefined
          ? ds.data
          : ds.response?.default !== undefined
            ? ds.response.default
            : undefined
      if (seed !== undefined) sm.setState(name, structuredClone(seed))
    }

    const themeVarMap: Record<string, string> = {
      background: "var(--color-background)",
      foreground: "var(--color-foreground)",
      card: "var(--color-card)",
      cardForeground: "var(--color-card-foreground)",
      popover: "var(--color-popover)",
      popoverForeground: "var(--color-popover-foreground)",
      primary: "var(--color-primary)",
      primaryForeground: "var(--color-primary-foreground)",
      secondary: "var(--color-secondary)",
      secondaryForeground: "var(--color-secondary-foreground)",
      muted: "var(--color-muted)",
      mutedForeground: "var(--color-muted-foreground)",
      accent: "var(--color-accent)",
      accentForeground: "var(--color-accent-foreground)",
      destructive: "var(--color-destructive)",
      destructiveForeground: "var(--color-destructive-foreground)",
      border: "var(--color-border)",
      input: "var(--color-input)",
      ring: "var(--color-ring)",
      sidebar: "var(--color-sidebar)",
      sidebarForeground: "var(--color-sidebar-foreground)",
      success: "oklch(0.545 0.175 142.5)",
      warning: "oklch(0.75 0.183 55.934)",
    }
    for (const [key, val] of Object.entries(themeVarMap)) {
      sm.setState(`theme.${key}`, val)
    }

    if (Object.keys(this.designSystem).length > 0) {
      for (const [tokenName, className] of Object.entries(
        this.designSystem,
      )) {
        sm.setState(`ds.${tokenName}`, className)
      }
      sm.registerNamespaceAlias("ds")
    }
  }

  private initSingleBlockState(blockName: string, block: BlockDef): void {
    const sm = this.stateManager

    const camelName = kebabToCamel(blockName)
    sm.registerNamespaceAlias(camelName)
    if (camelName !== blockName) sm.registerNamespaceAlias(blockName)

    const blockState = block.state as Record<string, unknown>

    const stateEntries = (blockState?.state ?? {}) as Record<string, unknown>
    for (const [key, varDef] of Object.entries(stateEntries)) {
      const resolved = resolveVarDefault(varDef)
      if (resolved !== undefined) {
        sm.registerDeclaration?.(key, { default: resolved })
        const existing = sm.get(key)
        if (
          existing != null &&
          typeof existing === "object" &&
          !Array.isArray(existing) &&
          resolved != null &&
          typeof resolved === "object" &&
          !Array.isArray(resolved)
        ) {
          sm.setState(key, {
            ...(existing as Record<string, unknown>),
            ...(resolved as Record<string, unknown>),
          })
        } else {
          sm.setState(key, resolved)
        }
      }
    }

    const localState = (blockState as Record<string, unknown>)?.localState
    if (localState && typeof localState === "object") {
      for (const [key, varDef] of Object.entries(
        localState as Record<string, unknown>,
      )) {
        const resolved = resolveVarDefault(varDef)
        if (resolved !== undefined) {
          sm.registerDeclaration?.(`local.${key}`, { default: resolved })
          sm.setState(`local.${key}`, resolved)
        }
      }
    }

    const propsVars = (blockState as Record<string, unknown>)?.props
    if (propsVars && typeof propsVars === "object") {
      for (const [key, varDef] of Object.entries(
        propsVars as Record<string, unknown>,
      )) {
        const resolved = resolveVarDefault(varDef)
        if (resolved !== undefined) {
          sm.registerDeclaration?.(`props.${key}`, { default: resolved })
          sm.setState(`props.${key}`, resolved)
        }
      }
    }

    const computed = (blockState?.computed ?? {}) as Record<
      string,
      string | { expression?: string }
    >
    for (const [key, val] of Object.entries(computed)) {
      const expr =
        typeof val === "string"
          ? val
          : typeof val === "object" &&
              val !== null &&
              "expression" in val
            ? (val as { expression?: string }).expression
            : null
      if (typeof expr === "string") {
        this.computedExprs.set(key, expr)
        const resolved = sm.resolve(expr)
        sm.setState(`computed.${key}`, resolved)
      }
    }

    const dataSources = block.data as Record<string, unknown>
    if (dataSources && typeof dataSources === "object") {
      const datasets = dataSources.datasets as Record<string, Record<string, unknown>> | undefined
      const sources = datasets
        ? materializeV4DatasetsForRuntime(datasets) as unknown as Record<string, unknown>
        : (dataSources.sources as Record<string, unknown>) ?? dataSources
      for (const [name, ds] of Object.entries(sources)) {
        if (!ds || typeof ds !== "object") continue
        const src = ds as Record<string, unknown>
        const type = src.type
        if (
          type !== "static" &&
          type !== "rest" &&
          type !== "restApi"
        )
          continue
        const response = src.response as { default?: unknown } | undefined
        const seed =
          src.data != null
            ? src.data
            : response && response.default !== undefined
              ? response.default
              : undefined
        if (seed !== undefined) {
          sm.registerDeclaration?.(name, {
            default: structuredClone(seed),
          })
          sm.setState(name, structuredClone(seed))
        }
      }

      const navRaw = dataSources.navigation as
        | Record<string, unknown>
        | undefined
      if (navRaw) {
        sm.setState("nav", {
          pages: navRaw.pages ?? [],
          brand: navRaw.brand ?? null,
          footer: navRaw.footer ?? [],
          tabs: navRaw.tabs ?? [],
        })
        sm.registerNamespaceAlias("nav")
      }
    }
  }

  private registerSingleBlockFlows(
    blockName: string,
    block: BlockDef,
  ): void {
    const rawFlows = block.flow as Record<string, unknown>
    const flowEntries =
      (rawFlows?.flows as Record<string, unknown>) ?? rawFlows ?? {}
    const ns = camelToKebab(block.meta?.namespace ?? blockName)
    const flows: FlowMap = {}
    for (const [key, flow] of Object.entries(flowEntries)) {
      if (typeof flow !== "object" || flow === null) continue
      const fullKey = key.includes(".") ? key : `${ns}.${key}`
      flows[fullKey] = flow as Flow
    }
    if (Object.keys(flows).length > 0) {
      this.actionEngine.registerFlows(flows)
    }
  }

  registerAllFlows(): void {
    const allFlows: FlowMap = {}
    for (const [blockName, block] of Object.entries(this.blocks)) {
      const rawFlows = block.flow as Record<string, unknown>
      const flowEntries =
        (rawFlows?.flows as Record<string, unknown>) ?? rawFlows ?? {}
      const ns = camelToKebab(block.meta?.namespace ?? blockName)
      for (const [key, flow] of Object.entries(flowEntries)) {
        if (typeof flow !== "object" || flow === null) continue
        const fullKey = key.includes(".") ? key : `${ns}.${key}`
        allFlows[fullKey] = flow as Flow
      }
    }
    this.actionEngine.registerFlows(allFlows)
    this.tickFlows = null // invalidate the cached tick-flow list — flow set changed
  }

  updateDesignSystem(designSystem: Record<string, string>): void {
    this.stateManager.replaceDesignSystemState(designSystem)
    for (const key of Object.keys(this.designSystem)) delete this.designSystem[key]
    Object.assign(this.designSystem, structuredClone(designSystem))
    this.notifyListeners()
  }

  private registerBlockBindings(block: BlockDef, blockNs: string): void {
    const ed = this.eventDispatcher

    walkElements(block.ui?.root as UiElementLike | undefined, (el) => {
      if (el.events) {
        for (const [eventType, handler] of Object.entries(el.events)) {
          if (typeof handler === "string") {
            ed.addBinding(el.id, eventType, {
              flow: handler,
              __ns: blockNs,
            })
          } else if (typeof handler === "object" && handler !== null) {
            const h = handler as EventRef
            if (h.flow) {
              ed.addBinding(el.id, eventType, {
                flow: h.flow,
                params: h.params,
                __ns: blockNs,
              })
            } else if (h.action) {
              ed.addBinding(el.id, eventType, {
                action: h.action,
                params: h.params,
                __ns: blockNs,
              })
            }
          }
        }
      }
    })

    const evts = block.events as Record<string, unknown>
    if (!evts) return

    const bindings = evts.bindings as Record<string, unknown> | undefined
    if (bindings && typeof bindings === "object") {
      for (const [key, binding] of Object.entries(bindings)) {
        // [#802 e8c2307cf port → Core] canonical `${id}:${event}` parse (null when colonIdx<=0),
        // shared with the studio/export builders via @cogo/types-v2 — behavior-identical to the
        // prior inline lastIndexOf(":"), kept in sync to avoid binding-key dialect drift.
        const parsed = parseBindingKey(key)
        if (parsed) {
          const { elementId, eventType } = parsed
          if (typeof binding === "string") {
            ed.addBinding(elementId, eventType, {
              flow: binding,
              __ns: blockNs,
            })
          } else if (typeof binding === "object" && binding !== null) {
            const b = binding as {
              flow?: string
              action?: string
              params?: Record<string, unknown>
            }
            if (b.flow) {
              ed.addBinding(elementId, eventType, {
                flow: b.flow,
                params: b.params,
                __ns: blockNs,
              })
            } else if (b.action) {
              ed.addBinding(elementId, eventType, {
                action: b.action,
                params: b.params,
                __ns: blockNs,
              })
            }
          }
        }
      }
    }

    // Register an `{ elementId: { eventType: binding } }` map. Shared by the v2 top-level
    // form and the generator's `events.elements` wrapper (see below). [#782 port → Core]
    const registerEventMap = (elementId: string, eventMap: unknown): void => {
      if (!eventMap || typeof eventMap !== "object") return
      for (const [eventType, binding] of Object.entries(
        eventMap as Record<string, unknown>,
      )) {
        if (typeof binding !== "object" || binding === null) continue
        const b = binding as {
          flow?: string
          action?: string
          params?: Record<string, unknown>
        }
        if (b.flow) {
          ed.addBinding(elementId, eventType, {
            flow: b.flow,
            params: b.params,
            __ns: blockNs,
          })
        } else if (b.action) {
          ed.addBinding(elementId, eventType, {
            action: b.action,
            params: b.params,
            __ns: blockNs,
          })
        }
      }
    }

    // v2: { elementId: { eventType: binding } } — skip the wrapper keys of the generator form.
    for (const [elementId, eventMap] of Object.entries(evts)) {
      if (
        elementId === "bindings" ||
        elementId === "elements" ||
        elementId === "lifecycle"
      )
        continue
      registerEventMap(elementId, eventMap)
    }

    // Generator form: { lifecycle, elements: { elementId: { eventType: binding } } }. The
    // agentic generator emits events under an `elements` wrapper; accept it natively so
    // generated blocks run without a separate conversion. (#782 da4ea8126 landed in
    // react-renderer-v2 app-runtime.ts but was missing from this extracted Core; the
    // lifecycle → init flows are handled by the block lifecycle manager, not here.)
    const elements = evts.elements as Record<string, unknown> | undefined
    if (elements && typeof elements === "object") {
      for (const [elementId, eventMap] of Object.entries(elements)) {
        registerEventMap(elementId, eventMap)
      }
    }
  }

  private registerBuiltinActions(platformAdapter: PlatformAdapter): void {
    const sm = this.stateManager
    const ae = this.actionEngine
    registerFormulaActions(ae)

    // navigate is intentionally NOT re-registered here. The canonical engine builtin
    // (ActionFlowEngine.actionNavigate) honors route/to/target + mode + params (query
    // string) + transition and dispatches through this.platform — which IS this same
    // platformAdapter (passed to the engine constructor above). The prior override
    // read only route+mode and therefore silently dropped params/transition/to in the
    // app path. Leave navigate to the builtin so those fields survive.
    // The platformAdapter routes pushRoute/replaceRoute through setRoute, which now
    // forwards the transition + push/replace intent on to the host router.

    registerReportDesignAction(ae, sm)
  }

  // ─── Private: datasource loading ──────────────────────────────────────────

  private restDataSourcesLoaded = false
  private async loadRestDataSources(): Promise<void> {
    if (this.restDataSourcesLoaded) return
    this.restDataSourcesLoaded = true

    const entries = Object.entries(
      this.manifest.datasources ?? {},
    ).filter(([, ds]) => ds.type === "rest" && ds.endpoint)
    if (entries.length === 0) return

    const sm = this.stateManager
    await Promise.all(
      entries.map(async ([name, ds]) => {
        try {
          const data = await this.dataSourceManager.loadSource(
            ds as unknown as ConstructorParameters<
              typeof DataSourceManager
            >[0][string],
            name,
          )
          sm.setStateTrusted(name, data)
        } catch (err) {
          console.warn(
            `[AppRuntimeCore] REST datasource "${name}" failed:`,
            err,
          )
          sm.setStateTrusted(`${name}_error`, String(err))
          if (ds.data == null) {
            const fallback = ds.response?.default
            if (fallback !== undefined)
              sm.setStateTrusted(name, structuredClone(fallback))
          }
        }
      }),
    )
  }

  private readonly blockRestLoaded = new Set<string>()
  private async loadBlockRestDataSources(
    blockName: string,
    block: BlockDef,
  ): Promise<void> {
    if (this.blockRestLoaded.has(blockName)) return
    this.blockRestLoaded.add(blockName)

    const dataSources = block.data as Record<string, unknown>
    if (!dataSources || typeof dataSources !== "object") return

    const datasets = dataSources.datasets as Record<string, Record<string, unknown>> | undefined
    const sources = datasets
      ? materializeV4DatasetsForRuntime(datasets) as unknown as Record<string, unknown>
      : (dataSources.sources as Record<string, unknown>) ?? dataSources
    const sm = this.stateManager

    for (const [name, ds] of Object.entries(sources)) {
      if (ds && typeof ds === "object") {
        this.dataSourceManager.registerSource(
          name,
          ds as unknown as ConstructorParameters<
            typeof DataSourceManager
          >[0][string],
        )
      }
    }

    const restEntries = Object.entries(sources).filter(([, ds]) => {
      if (!ds || typeof ds !== "object") return false
      const src = ds as Record<string, unknown>
      return src.type === "rest" && src.endpoint
    })
    if (restEntries.length === 0) return

    await Promise.all(
      restEntries.map(async ([name, ds]) => {
        const src = ds as DataSource
        try {
          const data = await this.dataSourceManager.loadSource(
            src as unknown as ConstructorParameters<
              typeof DataSourceManager
            >[0][string],
            name,
          )
          sm.setStateTrusted(name, data)
        } catch (err) {
          console.warn(
            `[AppRuntimeCore] Block "${blockName}" REST datasource "${name}" failed:`,
            err,
          )
          sm.setStateTrusted(`${name}_error`, String(err))
          if (src.data == null) {
            const fallback = src.response?.default
            if (fallback !== undefined)
              sm.setStateTrusted(name, structuredClone(fallback))
          }
        }
      }),
    )
  }

  // ─── Private: flow helpers ────────────────────────────────────────────────

  private findFlowBySuffix(flowName: string): Flow | undefined {
    const names = this.actionEngine.getRegisteredFlowNames()
    const match = names.find((k) => k.endsWith(`.${flowName}`))
    return match ? this.actionEngine.findFlow(match) : undefined
  }
}

// ─── UiElement-like minimal type for walkElements ────────────────────────────
// We only need id, events, and children — avoids importing UiElement from
// @cogo/types-v2 in places where we just walk the tree.

interface UiElementLike {
  id: string
  events?: Record<string, unknown>
  children?: UiElementLike[]
  template?: UiElementLike
}

// ─── Pure utility functions ───────────────────────────────────────────────────

const kebabToCamel = (s: string) =>
  s.replace(/-([a-z])/g, (_: string, c: string) => c.toUpperCase())
const camelToKebab = (s: string) =>
  s.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()

export function matchRouteCore(pattern: string, path: string): boolean {
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")
  if (patternParts.length !== pathParts.length) return false
  return patternParts.every((part, i) => {
    if (part.startsWith(":")) return true
    return part === pathParts[i]
  })
}

function scorePageSpecificity(pattern: string, path: string): number {
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")
  if (patternParts.length !== pathParts.length) return -1
  let score = 0
  for (let i = 0; i < patternParts.length; i++) {
    const part = patternParts[i]
    if (part.startsWith(":")) continue
    if (part !== pathParts[i]) return -1
    score++
  }
  return score
}

function extractRouteParams(
  pattern: string,
  path: string,
): Record<string, string> {
  const params: Record<string, string> = {}
  const patternParts = pattern.split("/")
  const pathParts = path.split("/")
  patternParts.forEach((part, i) => {
    if (part.startsWith(":") && pathParts[i]) {
      params[part.slice(1)] = pathParts[i]
    }
  })
  return params
}

function walkElements(
  element: UiElementLike | undefined | null,
  fn: (el: UiElementLike) => void,
): void {
  if (!element) return
  fn(element)
  element.children?.forEach((child) => walkElements(child, fn))
  if (element.template) walkElements(element.template, fn)
}

function executionBlockNamespace(context?: Record<string, unknown>): string | null {
  const value = context?.[ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY]
  return typeof value === "string" && value.length > 0 ? value : null
}

function resolveVarDefault(varDef: unknown): unknown {
  if (
    varDef != null &&
    typeof varDef === "object" &&
    !Array.isArray(varDef)
  ) {
    const def = varDef as Record<string, unknown>
    if (def.default !== undefined) return structuredClone(def.default)
    if (def.type === undefined) return structuredClone(varDef)
    return undefined
  }
  return varDef !== undefined ? structuredClone(varDef) : undefined
}

function resolveBindingParams(
  params: Record<string, unknown>,
  context: Record<string, unknown>,
  stateManager?: { resolve(expr: string): unknown },
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      const pureMatch = /^#\{([^}]+)\}$/.exec(value)
      if (pureMatch) {
        const contextVal = evaluateContextExpression(
          pureMatch[1].trim(),
          context,
        )
        if (contextVal !== undefined) {
          result[key] = contextVal
        } else if (stateManager) {
          const stateVal = stateManager.resolve(value)
          result[key] = stateVal !== value ? stateVal : undefined
        } else {
          result[key] = undefined
        }
      } else {
        result[key] = value
      }
    } else {
      result[key] = value
    }
  }
  return result
}

function evaluateContextExpression(
  expr: string,
  context: Record<string, unknown>,
): unknown {
  const pathValue = getFromContext(expr, context)
  if (pathValue !== undefined) return pathValue

  let processed = expr
  const refs: Array<{ match: string; value: unknown }> = []
  processed = expr.replace(/([a-zA-Z_][\w]*(?:\.[\w]+)*)/g, (match) => {
    const val = getFromContext(match, context)
    if (val !== undefined) {
      refs.push({ match, value: val })
      if (typeof val === "number") return val.toString()
      if (typeof val === "string") {
        const n = parseFloat(val)
        return isNaN(n) ? JSON.stringify(val) : n.toString()
      }
      return String(val)
    }
    return match
  })

  if (refs.length === 0) return undefined
  if (refs.length === 1 && expr.trim() === refs[0].match)
    return refs[0].value

  const trimmed = processed.trim()
  const numResult = parseFloat(trimmed)
  if (!isNaN(numResult) && /^-?\d+(\.\d+)?$/.test(trimmed))
    return numResult

  const binMatch =
    /^(-?\d+(?:\.\d+)?)\s*([+\-*/])\s*(-?\d+(?:\.\d+)?)$/.exec(trimmed)
  if (binMatch) {
    const l = parseFloat(binMatch[1])
    const r = parseFloat(binMatch[3])
    switch (binMatch[2]) {
      case "+":
        return l + r
      case "-":
        return l - r
      case "*":
        return l * r
      case "/":
        return r !== 0 ? l / r : 0
    }
  }

  return refs[0]?.value
}

function getFromContext(
  path: string,
  ctx: Record<string, unknown>,
): unknown {
  const keys = path.split(".")
  let current: unknown = ctx
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}
